import { ONLINE_CHANNEL_LABELS, ROLE_PERMISSIONS, type OnlineChannel, type OrderType, type Permission, type Role } from "./core";

/** Timestamps are ISO-8601 UTC strings written by the trusted engine clock. */
export type Iso = string;
export type BusinessDate = string;

export const COLLECTIONS = [
  "users", "staffSecrets", "settings", "menuCategories", "menuItems", "tables", "orders", "orderCounters",
  "idempotencyKeys", "kitchenTickets", "printJobs", "payments", "refunds", "promotions", "coupons",
  "couponRedemptions", "complimentaryApprovals", "customers", "loyaltyTransactions", "inventoryItems",
  "suppliers", "supplierPayments", "purchases", "stockMovements", "expenses", "shifts", "auditLogs", "schemaMigrations", "appReleases", "alerts", "notificationDevices",
  "tableCalls", "devices",
  // The accounts app (accounting.ts, engine/accounts.ts).
  "ledgerAccounts", "journalEntries", "employees", "payrollRuns", "accountingSettings", "bankReconciliations",
  // Back office (engine/backoffice.ts): attendance, supplier bills and purchase orders.
  "attendance", "staffRoster", "supplierBills", "purchaseOrders"
] as const;
export type CollectionName = (typeof COLLECTIONS)[number];

/** Collections that grow daily; every document carries `businessDate` so clients load a window, not the history. */
export const DATED_COLLECTIONS: readonly CollectionName[] = [
  "orders", "kitchenTickets", "printJobs", "payments", "refunds", "couponRedemptions", "complimentaryApprovals",
  "loyaltyTransactions", "purchases", "supplierPayments", "stockMovements", "expenses", "shifts", "auditLogs", "alerts", "tableCalls",
  "journalEntries", "payrollRuns", "attendance", "supplierBills", "purchaseOrders"
];
/** Never sent to any client. */
export const PRIVATE_COLLECTIONS: readonly CollectionName[] = ["staffSecrets", "idempotencyKeys", "orderCounters", "notificationDevices"];

export type User = {
  id: string; email: string; displayName: string; role: Role; permissions: Permission[];
  disabled: boolean; mustChangePassword: boolean; hasPin: boolean;
  /** True while an unused account-recovery code exists for this Super Admin. */
  hasRecoveryCode?: boolean;
  /** True once the account signs in with an authenticator app code as well (required for a Super Admin in cloud mode). */
  hasTotp?: boolean;
  createdAt: Iso; createdBy: string; updatedAt?: Iso; schemaVersion: number;
};
export type StaffSecret = {
  id: string; passwordHash?: string; passwordSalt?: string; pinHash?: string; pinSalt?: string; /** PBKDF2 rounds used for this PIN; older PINs without it used 60,000. */ pinIterations?: number; recoveryHash?: string; recoverySalt?: string; /** PBKDF2 rounds of the recovery code; codes without it used 150,000 (local mode only). */ recoveryIterations?: number;
  failedPinAttempts?: number; pinLockedUntil?: Iso;
  /** Authenticator app (TOTP) secret, the last 30-second step used (a code works once) and a secret still being set up. */
  totpSecret?: string; totpLastStep?: number; totpPending?: string;
  /** Devices (by device key) where this person entered the authenticator code, and until when that device is trusted. */
  trustedDevices?: Record<string, Iso>;
};
/** A computer or phone that signed in. With device approval on, only approved ones can (see engine/security.ts). */
export type Device = {
  id: string; label: string; status: "pending" | "approved" | "revoked"; requestedAt: Iso;
  lastUserId: string; lastUserName: string; lastUsedAt: Iso; approvedByName?: string; approvedAt?: Iso;
  /** Management accounts approved on this device (each person separately); cashiers only need the device approved. */
  approvedUsers?: { id: string; name: string }[];
  /** Management accounts that tried to sign in here and wait for approval. */
  pendingUsers?: { id: string; name: string; at: Iso }[];
  schemaVersion: number;
};

export type RoleThreshold = { maxDiscountBps: number; maxRefundMinor: number };
export type Settings = {
  id: "restaurant"; name: string; addressLines: string[]; phone: string; receiptFooter: string;
  taxBps: number; taxLabel: string; serviceChargeBps: number; serviceChargeDineInOnly: boolean;
  /** Off: the kitchen works from printed KOTs only and tickets need no on-screen status updates. */
  kitchenDisplayEnabled: boolean;
  /** Minutes without mouse or keyboard activity before the terminal signs the user out. 0 switches it off. */
  autoLockMinutes: number;
  /** Waiters who can be named on a dine-in bill. Plain names: waiters do not need a login. */
  waiterNames: string[];
  loyaltyEnabled: boolean; loyaltySpendPerPointMinor: number; loyaltyPointValueMinor: number;
  /** False while staff have paused ordering from the customer app. Absent means switched on. */
  onlineOrdersEnabled?: boolean;
  /** Opening hours as shown to customers in the ordering app, in the restaurant's own words ("Every day 11 am - 10 pm"). */
  openingHours?: string;
  /** Offers shown in the customer app and sent to phones as notifications. */
  appOffers?: AppOffer[];
  /**
   * Today's sold-out dishes, kept beside the settings as well as on each dish: the customer app reads the settings
   * often and cheaply, so a dish put back on sale shows as available within seconds.
   */
  soldOutToday?: { date: BusinessDate; itemIds: string[] };
  /** The table QR page: which buttons it shows, and whether it shows the table's bill and the menu. */
  bellOptions?: BellOptions;
  /** On: only computers and phones the Super Admin approved can sign in (cloud mode). */
  deviceApproval?: boolean;
  /** When device approval was last switched on. Devices first seen before it were in use already, and are not locked out by it. */
  deviceApprovalSince?: Iso;
  /** A manual discount of at least this share of the bill is reported to management. 0 switches the alert off. */
  alertDiscountBps: number;
  /** False: a dish whose ingredients have run out can still be sold (the stock goes below zero). Absent means true. */
  stockBlocksSale?: boolean;
  thresholds: Record<Role, RoleThreshold>; publicStatusBaseUrl: string;
  updatedAt?: Iso; schemaVersion: number;
};

export type AppOffer = { id: string; title: string; text: string; /** Last day shown (inclusive), or null for no end. */ until: BusinessDate | null; createdAt: Iso;
  /** The POS promotion that gives the discount. Without one, the offer is only a message. */
  promotionId?: string | null;
  /** The promotion was made by this offer (and ends with it), rather than an existing one being linked. */
  ownsPromotion?: boolean;
  /** Who sees it: the customer app, the table QR page (dine-in), or both. Absent on older offers: the app. */
  audience?: "app" | "dine_in" | "all" };
/** "App order" or "Web order", for an order a customer placed themselves; null for counter orders. */
export const onlineLabel = (order: Pick<Order, "source" | "channel">): string | null => order.source === "app" ? ONLINE_CHANNEL_LABELS[order.channel ?? "app"] : null;
export function promotionFitsOrder(promotion: Pick<Promotion, "channels">, order: { type: string; source?: string | undefined }): boolean {
  if (!promotion.channels || promotion.channels.length === 0) return true;
  const channel: PromotionChannel = order.source === "app" ? "app" : order.type === "dine_in" ? "dine_in" : "counter";
  return promotion.channels.includes(channel);
}
/**
 * An app order is ready to collect when staff said so, the kitchen screen marked it ready, or the ready time staff
 * gave has come. Until then the counter does not take its payment.
 */
export function appOrderReady(order: Pick<Order, "collectReadyAt" | "kitchenState" | "onlineState" | "readyAt">, now: Date, kitchenDisplayEnabled: boolean): boolean {
  return Boolean(order.collectReadyAt) || (kitchenDisplayEnabled && order.kitchenState === "ready") || (order.onlineState === "accepted" && Boolean(order.readyAt) && Date.parse(order.readyAt!) <= now.getTime());
}
/** `removed`: deleted from the screens by staff. The record stays so old bills, reports and stock history still read it. */
export type MenuCategory = { id: string; name: string; sortOrder: number; active: boolean; removed?: boolean; schemaVersion: number };
export type RecipeLine = { inventoryItemId: string; qtyMilli: number };
/** `recipe`: what one portion of this size uses. Absent (sizes saved before portions had recipes) means the dish's own recipe. */
export type MenuSize = { id: string; name: string; priceMinor: number; recipe?: RecipeLine[];
  /** Cost of one portion of this size when there is no recipe. Absent: the dish's own manual cost. */
  costMinor?: number };
/** `recipe`: the extra stock an add-on uses (extra cheese, an egg), on top of the dish. */
export type ModifierOption = { id: string; name: string; priceMinor: number; recipe?: RecipeLine[] };
export type ModifierGroup = { id: string; name: string; min: number; max: number; options: ModifierOption[] };
export type Availability = { days: number[]; fromMinutes: number; toMinutes: number };
export type MenuItem = {
  id: string; categoryId: string; name: string; description: string; priceMinor: number; costMinor: number;
  imageUrl: string; barcode: string; active: boolean; sizes: MenuSize[]; modifierGroups: ModifierGroup[];
  recipe: RecipeLine[]; availability?: Availability | null; sortOrder: number;
  /** The business date on which the dish ran out: the customer app shows it as sold out for that day only. */
  soldOutDate?: BusinessDate | null;
  removed?: boolean;
  createdAt: Iso; updatedAt: Iso; schemaVersion: number;
};

export type TableStatus = "available" | "occupied" | "reserved";
export type DiningTable = {
  id: string; name: string; area: string; seats: number; status: TableStatus; active: boolean;
  activeOrderId: string | null; waiterId: string | null; waiterName: string | null; reservedFor: string | null;
  sortOrder: number; schemaVersion: number;
  /** Secret in the table's QR code for the table bell. Changing it makes the old printed QR stop working. */
  bellKey?: string | null;
  /** Staff paused the bell at this table (e.g. it is being misused). */
  bellMuted?: boolean;
};

/** What a customer can ask for from the QR table bell. */
export const TABLE_CALL_TYPES = ["waiter", "water", "bill", "other", "custom"] as const;
export type TableCallType = (typeof TABLE_CALL_TYPES)[number];
/** A customer pressed the table bell. Only possible while the table has an open dine-in bill. */
export type TableCall = {
  id: string; tableId: string; tableName: string; orderId: string; dailyNumber: string; type: TableCallType; note: string;
  /** open → received (a staff member saw it and is coming) → done. The customer's phone shows each step. */
  status: "open" | "received" | "done"; createdAt: Iso; doneAt: Iso | null; doneBy: string | null; businessDate: BusinessDate; schemaVersion: number;
  receivedAt?: Iso | null; receivedBy?: string | null;
  /** The QR page button that was pressed, as the manager named it (absent on calls from before buttons could be named). */
  buttonId?: string; label?: string;
};
/**
 * A button on the table QR page. `kind` decides what it means to the system: "other" asks the customer for a
 * short note, "custom" is a request the manager made up ("Extra napkins"); the label is what everyone sees.
 */
export type BellButton = { id: string; kind: TableCallType; label: string; labelSi: string; icon: string };
export const DEFAULT_BELL_BUTTONS: BellButton[] = [
  { id: "waiter", kind: "waiter", label: "Call Waiter", labelSi: "වේටර් කැඳවන්න", icon: "🙋" },
  { id: "water", kind: "water", label: "Water", labelSi: "වතුර", icon: "💧" },
  { id: "bill", kind: "bill", label: "Bring the Bill", labelSi: "බිල ගෙනෙන්න", icon: "🧾" },
  { id: "other", kind: "other", label: "Other", labelSi: "වෙනත්", icon: "💬" }
];
/** What the table's QR page offers, set by a manager under Tables → QR codes. */
export type BellOptions = { buttons: BellButton[]; showBill: boolean; showMenu: boolean };
export const DEFAULT_BELL_OPTIONS: BellOptions = { buttons: DEFAULT_BELL_BUTTONS, showBill: true, showMenu: true };
/** The QR page options as stored, made whole: settings saved before buttons could be named listed only their kinds. */
export function bellOptionsOf(settings: { bellOptions?: unknown } | undefined): BellOptions {
  const stored = settings?.bellOptions as { buttons?: unknown[]; showBill?: boolean; showMenu?: boolean } | undefined;
  if (!stored) return DEFAULT_BELL_OPTIONS;
  const buttons = (stored.buttons ?? []).map((entry) => typeof entry === "string" ? DEFAULT_BELL_BUTTONS.find((button) => button.kind === entry) : entry as BellButton).filter((button): button is BellButton => Boolean(button));
  return { buttons: buttons.length > 0 ? buttons : DEFAULT_BELL_BUTTONS, showBill: stored.showBill ?? true, showMenu: stored.showMenu ?? true };
}

export type OrderLine = {
  id: string; menuItemId: string; name: string; sizeName: string | null;
  modifiers: { name: string; priceMinor: number }[]; unitPriceMinor: number; unitCostMinor: number;
  /** What was chosen, so the stock used can be worked out again on a void. Absent on lines saved before portion recipes. */
  sizeId?: string | null; modifierOptionIds?: string[];
  quantity: number; note: string; complimentary: boolean; discountMinor: number;
  kotBatch: number; voided: boolean; voidReason?: string; categoryId: string;
};
export type DiscountInput =
  | { kind: "promotion"; promotionId: string }
  | { kind: "coupon"; code: string }
  | { kind: "manual"; mode: "percent" | "fixed"; value: number; reason: string; lineId?: string | undefined }
  | { kind: "staff" }
  | { kind: "loyalty"; points: number };
export type AppliedDiscount = {
  kind: "promotion" | "coupon" | "manual" | "staff" | "loyalty" | "auto";
  label: string; amountMinor: number; scope: "bill" | "item";
  promotionId?: string; couponId?: string; code?: string; points?: number; reason?: string; bps?: number;
};
export type OrderTotals = {
  grossMinor: number; complimentaryMinor: number; lineDiscountMinor: number; billDiscountMinor: number;
  discountMinor: number; subtotalMinor: number; serviceChargeMinor: number; taxMinor: number;
  totalMinor: number; costMinor: number;
};
export type PaymentState = "unpaid" | "partial" | "paid" | "partially_refunded" | "refunded" | "complimentary";
export type KitchenState = "pending" | "preparing" | "ready" | "served";
export type Complimentary = { kind: string; reason: string; fullOrder: boolean; approvedBy: string; approvedByName: string; /** Who received it. Absent on older records. */ recipient?: string };
export type Order = {
  id: string; ref: string; publicToken: string; type: OrderType; dailyNumber: string; sequence: number;
  businessDate: BusinessDate; customerId: string | null; customerName: string; customerPhone: string;
  tableId: string | null; tableName: string | null;
  /** Name of the waiter serving a dine-in table, chosen from the restaurant's waiter list. Absent on older orders. */
  waiterId?: string | null; waiterName?: string | null;
  lines: OrderLine[]; discountInputs: DiscountInput[];
  discounts: AppliedDiscount[]; totals: OrderTotals; taxBps: number; serviceChargeBps: number;
  status: "open" | "completed" | "voided"; paymentState: PaymentState; kitchenState: KitchenState;
  fulfillmentState: "open" | "collected" | "served"; paidMinor: number; refundedMinor: number;
  /** Set on orders placed by a customer themselves (app or web); absent on orders entered by staff. */
  source?: "app";
  /** Which front end a customer order came from. Absent on customer orders from before the two were told apart (shown as app orders). */
  channel?: OnlineChannel;
  /** App orders wait as "pending" until staff accept them; only then does the kitchen get a ticket. Absent on counter orders. */
  onlineState?: "pending" | "accepted";
  /** When staff expect an accepted app order to be ready, and what they told the customer. */
  readyAt?: Iso; onlineMessage?: string; acceptedAt?: Iso;
  /** Set when staff tell the customer of an app order that it is waiting at the counter. */
  collectReadyAt?: Iso;
  complimentary: Complimentary | null; discountApprovedBy: string | null; note: string;
  /** The largest manual discount share already reported to management, so re-pricing does not repeat the alert. */
  alertedDiscountBps?: number;
  loyaltyPointsEarned: number; loyaltyPointsRedeemed: number; kotBatches: number;
  createdBy: string; createdByName: string; createdAt: Iso; updatedAt: Iso; completedAt?: Iso;
  collectedAt?: Iso; collectedBy?: string; voidedAt?: Iso; voidReason?: string;
  version: number; schemaVersion: number;
};

export type KitchenTicket = {
  id: string; orderId: string; dailyNumber: string; type: OrderType; tableName: string | null;
  customerPhone: string; batch: number; incremental: boolean; note: string;
  lines: { name: string; sizeName: string | null; modifiers: string[]; quantity: number; note: string; complimentary: boolean }[];
  status: "queued" | "preparing" | "ready" | "served" | "cancelled";
  createdAt: Iso; startedAt?: Iso; readyAt?: Iso; servedAt?: Iso; createdByName: string;
  businessDate: BusinessDate; schemaVersion: number;
};

export type PaymentMethod = "cash" | "card" | "bank_transfer";
export type Payment = {
  id: string; orderId: string; dailyNumber: string; method: PaymentMethod; amountMinor: number;
  tenderedMinor: number; changeMinor: number; reference: string; shiftId: string;
  createdBy: string; createdByName: string; createdAt: Iso; businessDate: BusinessDate; schemaVersion: number;
};
export type Refund = {
  id: string; orderId: string; dailyNumber: string; method: PaymentMethod; amountMinor: number; reason: string;
  shiftId: string; approvedBy: string; approvedByName: string; createdBy: string; createdByName: string;
  createdAt: Iso; businessDate: BusinessDate; schemaVersion: number;
};

export type PromotionKind = "percentage" | "fixed" | "happy_hour" | "bogo" | "combo" | "manager_special" | "staff" | "birthday";
/** Where a promotion applies: orders from the customer app, dine-in bills, or takeaway and call orders at the counter. */
export type PromotionChannel = "app" | "dine_in" | "counter";
export type Promotion = {
  /** Absent or empty: every kind of order. */
  channels?: PromotionChannel[];
  id: string; name: string; kind: PromotionKind; scope: "bill" | "item"; valueBps: number; valueMinor: number;
  itemIds: string[]; categoryIds: string[]; minSpendMinor: number; maxDiscountMinor: number;
  startDate: BusinessDate | null; endDate: BusinessDate | null; days: number[]; fromMinutes: number; toMinutes: number;
  buyQty: number; getQty: number; autoApply: boolean; stackable: boolean; active: boolean;
  createdAt: Iso; updatedAt: Iso; schemaVersion: number;
};
export type Coupon = {
  id: string; code: string; description: string; mode: "percent" | "fixed"; value: number;
  minSpendMinor: number; maxDiscountMinor: number; maxRedemptions: number; perCustomerLimit: number;
  redeemedCount: number; startDate: BusinessDate | null; endDate: BusinessDate | null; active: boolean;
  createdAt: Iso; updatedAt: Iso; schemaVersion: number;
};
export type CouponRedemption = {
  id: string; couponId: string; code: string; orderId: string; customerId: string | null;
  amountMinor: number; reversed: boolean; createdAt: Iso; businessDate: BusinessDate; schemaVersion: number;
};
export type ComplimentaryApproval = {
  /** The person the food or drink was given to. Absent on records made before this was required. */
  recipient?: string;
  id: string; orderId: string; dailyNumber: string; kind: string; reason: string; fullOrder: boolean;
  retailValueMinor: number; costMinor: number; items: { name: string; quantity: number; valueMinor: number }[];
  requestedBy: string; requestedByName: string; approvedBy: string; approvedByName: string;
  createdAt: Iso; businessDate: BusinessDate; schemaVersion: number;
};

export type Customer = {
  id: string; phone: string; name: string; birthday: string; notes: string; points: number;
  totalSpentMinor: number; orderCount: number; lastOrderAt: Iso | null; marketingConsent: boolean;
  active: boolean; createdAt: Iso; updatedAt: Iso; schemaVersion: number;
  /** Staff have barred this phone number from ordering in the customer app (for example after a fake order). */
  blockedFromApp?: boolean; blockedReason?: string;
};
export type LoyaltyTransaction = {
  id: string; customerId: string; orderId: string | null; kind: "earn" | "redeem" | "reverse" | "restore" | "adjust";
  points: number; balanceAfter: number; reason: string; createdBy: string; createdAt: Iso;
  businessDate: BusinessDate; schemaVersion: number;
};

/** Stock quantities are integer thousandths of the item's unit (1.250 kg = 1250). */
export type InventoryItem = {
  id: string; name: string; unit: string; stockMilli: number; reorderMilli: number; avgCostMinor: number;
  /** The level a full re-stock brings the item back up to. Absent or not above the reorder level: twice the reorder level. */
  parMilli?: number;
  removed?: boolean;
  /** How it is bought, besides the unit itself: "Bag" = 25 kg. Purchases can be entered in these. */
  purchaseUnits?: PurchaseUnit[];
  supplierId: string | null; active: boolean; createdAt: Iso; updatedAt: Iso; schemaVersion: number;
};
export type PurchaseUnit = { name: string; milli: number };
/** At or below its reorder level: management is warned before it runs out. Items without a reorder level are never "low". */
export const isLowStock = (item: Pick<InventoryItem, "active" | "stockMilli" | "reorderMilli">) => item.active && item.reorderMilli > 0 && item.stockMilli <= item.reorderMilli;
/** How much to buy to bring the item back up to its full level. */
export function restockMilli(item: Pick<InventoryItem, "stockMilli" | "reorderMilli" | "parMilli">): number {
  const target = item.parMilli && item.parMilli > item.reorderMilli ? item.parMilli : item.reorderMilli * 2;
  return Math.max(0, target - item.stockMilli);
}
/**
 * The stock one portion uses: the chosen size's recipe (or the dish's own when the size has none) plus each chosen
 * add-on's. The same ingredient appearing twice is added together.
 */
export function portionRecipe(item: Pick<MenuItem, "recipe" | "sizes" | "modifierGroups">, sizeId: string | null | undefined, optionIds: readonly string[] = []): RecipeLine[] {
  const size = sizeId ? item.sizes.find((entry) => entry.id === sizeId) : undefined;
  const options = item.modifierGroups.flatMap((group) => group.options).filter((option) => optionIds.includes(option.id));
  const totals = new Map<string, number>();
  for (const part of [...(size?.recipe ?? item.recipe), ...options.flatMap((option) => option.recipe ?? [])]) totals.set(part.inventoryItemId, (totals.get(part.inventoryItemId) ?? 0) + part.qtyMilli);
  return [...totals].map(([inventoryItemId, qtyMilli]) => ({ inventoryItemId, qtyMilli }));
}
export type Supplier = { id: string; name: string; phone: string; notes: string; active: boolean; removed?: boolean;
  /** What the restaurant currently owes this supplier. Below zero: an advance paid ahead of bills. */ balanceMinor?: number;
  contactPerson?: string; email?: string; address?: string; /** For sending purchase orders; the phone number when empty. */ whatsapp?: string;
  schemaVersion: number };
export type SupplierPayment = {
  /** Paid ahead of bills (the balance can go below zero). */ advance?: boolean;
  id: string; supplierId: string; supplierName: string; amountMinor: number; method: PaymentMethod; shiftId: string | null; note: string;
  balanceAfterMinor: number; createdBy: string; createdByName: string; createdAt: Iso; businessDate: BusinessDate; schemaVersion: number;
};
export type Purchase = {
  /** Bought on credit: the total was added to what is owed to the supplier instead of being paid at once. */
  onCredit?: boolean;
  id: string; supplierId: string | null; supplierName: string; invoiceNo: string;
  lines: { inventoryItemId: string; name: string; qtyMilli: number; unitCostMinor: number; lineMinor: number }[];
  /** The stock's cost, before VAT. */ totalMinor: number; /** Input VAT on the invoice, owed or paid on top of `totalMinor`. */ vatMinor?: number; createdBy: string; createdByName: string; createdAt: Iso; businessDate: BusinessDate; schemaVersion: number;
};
export type StockMovementKind = "purchase" | "sale" | "complimentary" | "waste" | "adjustment" | "void_return" | "count";
export type StockMovement = {
  id: string; inventoryItemId: string; itemName: string; kind: StockMovementKind; deltaMilli: number;
  balanceAfterMilli: number; unitCostMinor: number; refType: string; refId: string; reason: string;
  createdBy: string; createdByName: string; createdAt: Iso; businessDate: BusinessDate; schemaVersion: number;
};

export type Expense = {
  id: string; category: string; description: string; amountMinor: number; method: PaymentMethod;
  /** A salary advance: who it was given to (a staff roster id). Payroll takes it off that person's next salary. */
  employeeId?: string; employeeName?: string;
  shiftId: string | null; voided: boolean; voidReason?: string; createdBy: string; createdByName: string;
  createdAt: Iso; businessDate: BusinessDate; schemaVersion: number;
};
export type Shift = {
  id: string; status: "open" | "closed"; openedBy: string; openedByName: string; openedAt: Iso;
  /** closedByName: the person who counted the drawer and closed it (named on purpose; terminals are often shared). closedOnAccount: the login that was used, when different. */
  openingFloatMinor: number; closedBy?: string; closedByName?: string; closedOnAccount?: string; closedAt?: Iso;
  countedCashMinor?: number; expectedCashMinor?: number; varianceMinor?: number;
  summary?: ShiftSummary; note: string; businessDate: BusinessDate; schemaVersion: number;
  /** Closed by the midnight job, without a drawer count. */
  autoClosed?: boolean;
};
export type ShiftSummary = {
  payments: Record<PaymentMethod, number>; refunds: Record<PaymentMethod, number>;
  cashExpensesMinor: number; orderCount: number;
};

export type PrintJob = {
  id: string; kind: "kot" | "receipt"; orderId: string; dailyNumber: string; ticketId: string | null;
  printerName: string; state: "queued" | "printed" | "failed" | "unknown"; attempts: number; message: string;
  reprint: boolean; createdBy: string; createdByName: string; createdAt: Iso; updatedAt: Iso;
  businessDate: BusinessDate; schemaVersion: number;
};
export type AuditLog = {
  /** Position in the tamper-evident chain, the previous entry's hash and this entry's hash. Absent on entries written before the chain existed. */
  seq?: number; prevHash?: string; hash?: string;
  id: string; actorId: string; actorName: string; actorRole: string; action: string; targetType: string;
  targetId: string; summary: Record<string, unknown>; reason: string; approvedBy: string;
  correlationId: string; createdAt: Iso; businessDate: BusinessDate; schemaVersion: number;
};

/** Something management should know about now: shown in the Management View and sent to their phones. */
export type Alert = {
  /** "system": the server itself failed (raised at most once every half hour). */
  id: string; kind: "day_end" | "void" | "refund" | "discount" | "complimentary" | "low_stock" | "daily_summary" | "system"; title: string; body: string;
  orderId: string | null; actorName: string; createdAt: Iso; businessDate: BusinessDate; schemaVersion: number;
};
/** A phone allowed to fetch alerts in the background. Only the hash of its token is stored. */
export type NotificationDevice = {
  id: string; userId: string; userName: string; label: string; tokenHash: string; revoked: boolean; createdAt: Iso; schemaVersion: number;
};

export type CollectionTypes = {
  users: User; staffSecrets: StaffSecret; settings: Settings; menuCategories: MenuCategory; menuItems: MenuItem;
  tables: DiningTable; orders: Order; orderCounters: { id: string; businessDate: string; type: string; sequence: number; hash?: string };
  idempotencyKeys: { id: string; command: string; result: unknown; createdAt: Iso };
  kitchenTickets: KitchenTicket; printJobs: PrintJob; payments: Payment; refunds: Refund; promotions: Promotion;
  coupons: Coupon; couponRedemptions: CouponRedemption; complimentaryApprovals: ComplimentaryApproval;
  customers: Customer; loyaltyTransactions: LoyaltyTransaction; inventoryItems: InventoryItem; suppliers: Supplier;
  purchases: Purchase; supplierPayments: SupplierPayment; stockMovements: StockMovement; expenses: Expense; shifts: Shift; auditLogs: AuditLog;
  schemaMigrations: { id: string; appliedAt: Iso; [key: string]: unknown };
  appReleases: { id: string; [key: string]: unknown };
  alerts: Alert; notificationDevices: NotificationDevice; tableCalls: TableCall; devices: Device;
  ledgerAccounts: LedgerAccount; journalEntries: JournalEntry; employees: Employee; payrollRuns: PayrollRun; accountingSettings: AccountingSettings; bankReconciliations: BankReconciliation;
  attendance: AttendanceRecord; staffRoster: StaffRosterEntry; supplierBills: SupplierBill; purchaseOrders: PurchaseOrder;
};

// ---------------------------------------------------------------- back office

export const ATTENDANCE_STATUSES = ["present", "absent", "half", "leave", "holiday", "off"] as const;
export type AttendanceStatus = (typeof ATTENDANCE_STATUSES)[number];
/**
 * One employee's day. The POS (clock in/out) and the accounts app (daily entry) write the same record, keyed by employee and
 * date, so whichever was used last is what the salary is worked out from.
 */
export type AttendanceRecord = {
  id: string; employeeId: string; employeeName: string; businessDate: BusinessDate; status: AttendanceStatus;
  clockIn: Iso | null; clockOut: Iso | null; /** Overtime in minutes, entered by the accountant. */ otMinutes: number; note: string;
  source: "pos" | "accounts"; updatedBy: string; updatedByName: string; updatedAt: Iso; schemaVersion: number;
};
/** Who can be clocked in on the POS: payroll staff without their salaries (which only the accounts app reads). */
export type StaffRosterEntry = { id: string; name: string; code: string; active: boolean; shiftStart?: string | null; schemaVersion: number };
/**
 * A supplier's bill for goods or services not recorded as a stock purchase (gas, repairs, a service, an invoice entered
 * later). It adds to what is owed to the supplier. Edited in place (each edit audited); only a Super Admin voids one.
 */
export type SupplierBill = {
  id: string; number: string; supplierId: string; supplierName: string; invoiceNo: string; businessDate: BusinessDate; dueDate: BusinessDate | null;
  /** "stock": goods for the kitchen (cost of sales); "expense": a running cost, posted by its category. */
  kind: "stock" | "expense"; category: string; description: string;
  /** The bill's total, VAT included. */ amountMinor: number; /** Input VAT on it, when the supplier is VAT registered. */ vatMinor: number;
  voided: boolean; voidReason?: string; voidedByName?: string;
  source: "pos" | "accounts"; createdBy: string; createdByName: string; createdAt: Iso; updatedAt: Iso; updatedByName: string; schemaVersion: number;
};
export type PurchaseOrderLine = { inventoryItemId: string; name: string; unit: string; qtyMilli: number; unitCostMinor: number };
/**
 * An order sent to a supplier. A draft can be changed by whoever made it; once sent (printed or sent on WhatsApp) only the
 * accountant or a Super Admin can change it. Receiving it records the purchase: stock goes up and, on credit, the bill is owed.
 */
export type PurchaseOrder = {
  id: string; number: string; supplierId: string; supplierName: string; lines: PurchaseOrderLine[]; note: string; totalMinor: number;
  status: "draft" | "sent" | "received" | "cancelled"; sentVia: ("print" | "whatsapp")[]; sentAt: Iso | null;
  receivedAt: Iso | null; purchaseId: string | null; cancelReason?: string;
  source: "pos" | "accounts"; createdBy: string; createdByName: string; createdAt: Iso; updatedAt: Iso; updatedByName: string; businessDate: BusinessDate; schemaVersion: number;
};

// ---------------------------------------------------------------- accounts app

export type AccountType = "asset" | "liability" | "equity" | "income" | "expense";
/** An account in the chart of accounts. `key` marks the accounts the POS figures are posted to (accounting.ts, SYSTEM_KEYS). */
export type LedgerAccount = {
  id: string; code: string; name: string; type: AccountType; key?: string; active: boolean;
  createdAt: Iso; updatedAt: Iso; schemaVersion: number;
};
export type JournalLine = { accountId: string; debitMinor: number; creditMinor: number; memo: string };
export type JournalSource = "manual" | "opening" | "payroll" | "payroll_payment" | "reversal" | "stock_adjustment";
/**
 * A journal the accountant posted. The POS figures are never stored as journals: they are worked out from the POS records,
 * so the books always agree with the POS. Never edited or deleted: a mistake is corrected by a reversing entry.
 */
export type JournalEntry = {
  id: string; number: string; businessDate: BusinessDate; memo: string; reference: string; lines: JournalLine[];
  source: JournalSource; sourceId: string | null; reversalOf: string | null; reversedBy: string | null;
  createdBy: string; createdByName: string; createdAt: Iso; schemaVersion: number;
};
export type EmployeeAllowance = { name: string; amountMinor: number; /** Counted in the earnings EPF and ETF are worked out on. */ epfLiable: boolean };
/** Payroll staff. Not every employee has a POS login, so they are kept apart from `users`. */
export type Employee = {
  id: string; code: string; name: string; nic: string; epfNo: string; designation: string; joinedOn: BusinessDate | null;
  basicMinor: number; allowances: EmployeeAllowance[]; bankName: string; bankAccount: string; active: boolean;
  /** When the person's work starts ("08:00", Colombo time); arriving later than it (plus the grace minutes) is late. */
  shiftStart?: string | null;
  createdAt: Iso; updatedAt: Iso; schemaVersion: number;
};
export type PayrollLine = {
  /** Days not worked and not paid (absent = 1, half day = 0.5), and what they took off the pay. Absent on older runs. */
  noPayDays?: number; noPayMinor?: number;
  employeeId: string; code: string; name: string; nic: string; epfNo: string; designation: string; bankAccount: string;
  basicMinor: number; allowances: EmployeeAllowance[]; overtimeMinor: number; serviceChargeMinor: number; otherEarningsMinor: number;
  grossMinor: number; epfBaseMinor: number; epfEmployeeMinor: number; epfEmployerMinor: number; etfMinor: number; apitMinor: number;
  advanceMinor: number; otherDeductionMinor: number; netMinor: number;
};
export type PayrollTotals = Pick<PayrollLine, "grossMinor" | "epfBaseMinor" | "epfEmployeeMinor" | "epfEmployerMinor" | "etfMinor" | "apitMinor" | "advanceMinor" | "otherDeductionMinor" | "netMinor" | "serviceChargeMinor">;
/** One month's salaries. `businessDate` is the month's last day, the date its journal is posted on. */
export type PayrollRun = {
  id: string; month: string; businessDate: BusinessDate; status: "posted" | "paid" | "voided"; lines: PayrollLine[]; totals: PayrollTotals;
  journalId: string; paymentJournalId: string | null; paidOn: BusinessDate | null; voidReason?: string;
  createdBy: string; createdByName: string; createdAt: Iso; updatedAt: Iso; schemaVersion: number;
};
/**
 * One bank statement ticked off against the books: which bank account lines (by `bankLineKey`, with the amount they had)
 * appeared on the statement up to its date, and the statement's closing balance.
 */
export type BankReconciliation = {
  id: string; accountId: string; statementDate: BusinessDate; statementBalanceMinor: number; cleared: Record<string, number>;
  differenceMinor: number; createdBy: string; createdByName: string; createdAt: Iso; schemaVersion: number;
};
/** A band of the monthly APIT table: the next `widthMinor` of taxable pay (null = all the rest) is taxed at `bps`. */
export type TaxSlab = { widthMinor: number | null; bps: number };
export type AccountingSettings = {
  id: "accounting";
  /** The first day the books cover. POS records before it are not posted; the opening balances journal is dated on it. */
  booksStartDate: BusinessDate;
  /** Days up to and including this one are closed: nothing can be posted or reversed on them. */
  lockedThrough: BusinessDate | null;
  /** Which account each POS figure is posted to (SYSTEM_KEYS key -> account id). Missing keys use the account carrying that key. */
  mapping: Record<string, string>;
  /** POS expense category -> expense account. Categories not listed go to the "Other expenses" account. */
  expenseAccounts: Record<string, string>;
  /**
   * How food cost is booked. "periodic" (default): purchases go straight to cost of sales and a month-end stock count journal
   * corrects it. "perpetual": purchases go to stock and each bill's recipe cost moves out of stock, which needs every dish
   * to have a recipe or cost and every ingredient to be bought through the POS.
   */
  costing?: "periodic" | "perpetual";
  /** Shows the VAT & SSCL screen and figures. Off (absent) while the restaurant is not VAT registered. */
  vatEnabled?: boolean;
  vatBps: number; ssclBps: number; /** Share of turnover SSCL is charged on (10,000 = all of it). */ ssclLiableBps: number;
  epfEmployeeBps: number; epfEmployerBps: number; etfBps: number; /** Monthly pay free of APIT. */ apitReliefMinor: number; apitSlabs: TaxSlab[];
  /** A day's pay is the monthly salary divided by this (absent days are taken off). Absent: 30. */ noPayDivisor?: number;
  /** An hour's pay is the monthly salary divided by this, and overtime is paid at otRateBps of it. Absent: 240 and 150%. */ otDivisor?: number; otRateBps?: number;
  /** A normal day at work, meal break included; time beyond it between arrival and leaving is overtime. Absent: 9 hours. */ normalDayMinutes?: number;
  /** Minutes after the shift start before an arrival counts as late. Absent: 10. */ lateGraceMinutes?: number;
  employerName: string; tin: string; vatNo: string; epfRegNo: string; etfRegNo: string;
  updatedAt: Iso; schemaVersion: number;
};

export const DEFAULT_SETTINGS: Settings = {
  id: "restaurant", name: "Mango Moons Restaurant", addressLines: ["Kottawa, Sri Lanka"], phone: "",
  receiptFooter: "Thank you for dining with Mango Moons!", taxBps: 0, taxLabel: "Tax",
  serviceChargeBps: 1000, serviceChargeDineInOnly: true, kitchenDisplayEnabled: false, autoLockMinutes: 15, waiterNames: [], loyaltyEnabled: true,
  loyaltySpendPerPointMinor: 10_000, loyaltyPointValueMinor: 100, alertDiscountBps: 2_000,
  thresholds: {
    super_admin: { maxDiscountBps: 10_000, maxRefundMinor: Number.MAX_SAFE_INTEGER },
    manager: { maxDiscountBps: 5_000, maxRefundMinor: 5_000_000 },
    viewer: { maxDiscountBps: 0, maxRefundMinor: 0 },
    accountant: { maxDiscountBps: 0, maxRefundMinor: 0 },
    cashier: { maxDiscountBps: 1_000, maxRefundMinor: 0 },
    waiter: { maxDiscountBps: 0, maxRefundMinor: 0 },
    kitchen: { maxDiscountBps: 0, maxRefundMinor: 0 }
  },
  publicStatusBaseUrl: "", schemaVersion: 1
};

/**
 * Who may read each collection. "any" is every signed-in staff member, "none" is backend-only.
 * Firestore Security Rules are generated from this table, and the client uses it to decide what to subscribe to.
 */
export const READ_ACCESS: Record<CollectionName, Permission | readonly Permission[] | "any" | "none"> = {
  users: "any", staffSecrets: "none", settings: "any", menuCategories: "any", menuItems: "any", tables: "any",
  orders: "orders.read", orderCounters: "none", idempotencyKeys: "none", kitchenTickets: "kitchen.read",
  printJobs: "orders.read", payments: "orders.read", refunds: "orders.read", promotions: "any",
  coupons: "promotions.manage", couponRedemptions: "promotions.manage", complimentaryApprovals: "reports.read",
  customers: "customers.read", loyaltyTransactions: "customers.read", inventoryItems: "orders.read",
  suppliers: ["inventory.manage", "accounts.manage"], supplierPayments: ["shifts.manage", "accounts.manage"],
  purchases: ["inventory.manage", "accounts.manage"], stockMovements: ["inventory.manage", "accounts.manage"],
  expenses: "reports.read", shifts: "orders.read", auditLogs: "audit.read", schemaMigrations: "none", appReleases: "any",
  alerts: "reports.read", notificationDevices: "none", tableCalls: "orders.read", devices: "staff.manage",
  ledgerAccounts: "accounts.manage", journalEntries: "accounts.manage", employees: "accounts.manage", payrollRuns: "accounts.manage", accountingSettings: "accounts.manage", bankReconciliations: "accounts.manage",
  attendance: ["attendance.clock", "accounts.manage"], staffRoster: ["attendance.clock", "accounts.manage"],
  supplierBills: ["inventory.manage", "accounts.manage"], purchaseOrders: ["inventory.manage", "accounts.manage"]
};
/** The permissions that each let a role read the collection ([] for "any" and "none"). */
export function readPermissions(collection: CollectionName): readonly Permission[] {
  const access = READ_ACCESS[collection];
  return access === "any" || access === "none" ? [] : typeof access === "string" ? [access] : access;
}
/** Whether this person may read the collection, by role or by a permission granted to them individually. */
export function canReadCollection(user: { role: Role; permissions: readonly Permission[] }, collection: CollectionName): boolean {
  const access = READ_ACCESS[collection];
  if (access === "none" || PRIVATE_COLLECTIONS.includes(collection)) return false;
  return access === "any" || readPermissions(collection).some((permission) => ROLE_PERMISSIONS[user.role].includes(permission) || user.permissions.includes(permission));
}
